import type React from 'react'
import { useEffect, useState } from 'react'
import type { IEvaluationProject } from '../../../Model/Project/IEvaluationProject.js'
import { useDispatch } from 'react-redux'
import { Actions } from '../../../Flux/Slice/index.js'
import { projectSlice } from '../../../Flux/Slice/Project/ProjectSlice.js'
import type { BaseModalProps } from '../../Modal/BaseModal.js'
import { BaseModal } from '../../Modal/BaseModal.js'

export interface InviteParticipantModalProps {
  project: IEvaluationProject
}

export const InviteParticipantModal: React.FC<InviteParticipantModalProps & BaseModalProps> = ({ isOpen, onClose, project }) => {
  const dispatch = useDispatch()

  // Form state
  const [participantEmail, setParticipantEmail] = useState('')
  const [additionalText, setAdditionalText] = useState('')
  const [mayEdit, setMayEdit] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset form when modal is opened
  useEffect(() => {
    if (isOpen) {
      setParticipantEmail('')
      setAdditionalText('')
      setMayEdit(false)
      setError(null)
    }
  }, [isOpen])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Validate form
    if (!participantEmail) {
      setError('Email is required')
      return
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(participantEmail)) {
      setError('Please enter a valid email address')
      return
    }

    setIsSubmitting(true)
    setError(null)

    // Dispatch action to invite participant
    dispatch(
      Actions[projectSlice.name].inviteProjectParticipantRequest({
        projectId: project.id,
        participantEmail,
        additionalText,
        mayEdit
      })
    )

    // Close modal and reset form
    onClose()
    setIsSubmitting(false)
  }

  return (
    <BaseModal isOpen={isOpen} onClose={onClose}>
      <div className="modal-box h-auto max-h-[90vh] overflow-auto">
        <div className="p-6">
          <h3 className="text-lg font-bold">Invite Participant to {project.name}</h3>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="p-6 pt-0">
            {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-sm mb-4">{error}</div>}

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="participantEmail">
                Participant Email *
              </label>
              <input
                data-testid={'participant-email'}
                id="participantEmail"
                type="email"
                className="shadow appearance-none border rounded-sm w-full py-2 px-3 text-gray-700 leading-tight focus:outline-hidden focus:shadow-outline"
                value={participantEmail}
                onChange={(e) => setParticipantEmail(e.target.value)}
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="additionalText">
                Additional Message
              </label>
              <textarea
                id="additionalText"
                className="shadow appearance-none border rounded-sm w-full py-2 px-3 text-gray-700 leading-tight focus:outline-hidden focus:shadow-outline"
                rows={4}
                value={additionalText}
                onChange={(e) => setAdditionalText(e.target.value)}
                placeholder="Add a personal message to the invitation email"
              />
            </div>

            <div className="mb-4">
              <label className="flex items-center">
                <input
                  data-testid={'allow-edit'}
                  type="checkbox"
                  className="form-checkbox h-5 w-5 text-blue-600"
                  checked={mayEdit}
                  onChange={(e) => setMayEdit(e.target.checked)}
                />
                <span className="ml-2 text-gray-700">Allow participant to edit the project</span>
              </label>
            </div>

            <div className="flex items-center justify-end">
              <button
                type="button"
                className="mr-4 inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                onClick={() => onClose()}
              >
                Cancel
              </button>
              <button
                data-testid={'invite-user-button'}
                type="submit"
                className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending Invitation...' : 'Send Invitation'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </BaseModal>
  )
}
